import { useSyncExternalStore } from "react";
import {
  DEFAULT_MODULE_LABEL,
  moduleSlug,
  normalizeModuleLabel,
  relabel,
} from "../../services/backend/gfunctions/pqrsd/_shared/moduleLabel";

// The institution's name for the PQRSD module (Config → PQRSD → General,
// stored as config/pqrsd.moduleLabel). `pqrsdText("Informes PQRSD")` returns
// the text with that name, synchronously, so it works in render, in handlers
// and in module-level maps read at render time.
//
// The value is cached in localStorage so the first paint already uses it; the
// live config doc keeps it current. App remounts the router when it changes
// (route elements are constants and would not re-render on their own).

const CACHE_KEY = "pqrsd.moduleLabel";

const readCache = () => {
  try {
    return normalizeModuleLabel(window.localStorage.getItem(CACHE_KEY));
  } catch (_) {
    return DEFAULT_MODULE_LABEL;
  }
};

let current = typeof window === "undefined" ? DEFAULT_MODULE_LABEL : readCache();
// True once the config doc answered (or could not): until then a URL with a
// renamed slug may simply not be routed yet, and must not be sent to /home.
let ready = false;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

function markReady() {
  if (ready) return;
  ready = true;
  notify();
}

function setLabel(raw) {
  const next = normalizeModuleLabel(raw);
  try {
    window.localStorage.setItem(CACHE_KEY, next);
  } catch (_) {
    /* private mode: the live doc still applies it */
  }
  if (next === current) return;
  current = next;
  notify();
}

export const getPqrsdLabel = () => current;

export const pqrsdText = (text) => relabel(text, current);

// URL segment of the citizen pages ("pqrsd" by default) and their paths:
// pqrsdPublicPath() → "/pqrsd", pqrsdPublicPath("estado") → "/pqrsd/estado".
export const getPqrsdSlug = () => moduleSlug(current);
export const pqrsdPublicPath = (page = "") => `/${getPqrsdSlug()}${page ? `/${page}` : ""}`;
export const isPqrsdLabelReady = () => ready;

// Starts listening to config/pqrsd once; safe to call repeatedly. Firebase
// is loaded here, not at import: every screen imports pqrsdText, and most of
// them (and their tests) have no reason to pull in the SDK for it.
let started = false;
export function startPqrsdLabel() {
  if (started) return;
  started = true;
  Promise.all([import("firebase/firestore"), import("../../config/ConfigFirebase")])
    .then(([{ doc, onSnapshot }, { firestore }]) => {
      onSnapshot(
        doc(firestore, "config", "pqrsd"),
        (snap) => {
          setLabel(snap.exists() ? snap.data().moduleLabel : "");
          markReady();
        },
        // Unreadable (offline, future rules): keep the cached/default name.
        () => markReady()
      );
    })
    .catch(() => markReady());
  // Never leave a page waiting on a config that does not answer.
  setTimeout(markReady, 4000);
}

// Applies a label right away (after the admin saves), without waiting for
// the snapshot round trip.
export const applyPqrsdLabel = (raw) => setLabel(raw);

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export function usePqrsdLabel() {
  return useSyncExternalStore(subscribe, getPqrsdLabel, () => DEFAULT_MODULE_LABEL);
}

export function usePqrsdLabelReady() {
  return useSyncExternalStore(subscribe, isPqrsdLabelReady, () => true);
}
